import React from "react";
import { Box, Avatar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronsUpDown, Settings } from "lucide-react";

/**
 * Sidebar v2 — modern SaaS sidebar.
 *
 * Props:
 *   navSections: [{ label, items: [{ icon, label, to, badge?, disabled? }] }]
 *   workspace:   { name, avatarUrl?, onClick? }
 *   user:        { name, email, avatarUrl?, onMenuClick? }
 *   onItemClick?(item)  — called in addition to navigation; useful for mobile drawer close
 *
 * Stays dumb: caller decides routes, icons, and handlers.
 */

function SectionLabel({ children }) {
  return (
    <Box
      sx={{
        px: "12px",
        pt: "16px",
        pb: "6px",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "#64748b", // slate-500
      }}
    >
      {children}
    </Box>
  );
}

function NavItem({ item, active, onClick }) {
  const Icon = item.icon;
  return (
    <Box
      role="button"
      tabIndex={item.disabled ? -1 : 0}
      onClick={item.disabled ? undefined : onClick}
      onKeyDown={(e) => {
        if (!item.disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick?.();
        }
      }}
      sx={{
        position: "relative",
        mx: "8px",
        my: "2px",
        px: "12px",
        height: 36,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        borderRadius: "8px",
        cursor: item.disabled ? "not-allowed" : "pointer",
        color: active ? "#0f172a" : "#475569", // slate-900 / slate-600
        backgroundColor: active ? "#f1f5f9" : "transparent", // slate-100
        opacity: item.disabled ? 0.5 : 1,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        transition: "background-color 150ms ease-out, color 150ms ease-out",
        "&:hover": {
          backgroundColor: item.disabled ? "transparent" : active ? "#f1f5f9" : "#f8fafc",
        },
        "&:focus-visible": {
          outline: "2px solid #f97316",
          outlineOffset: 2,
        },
      }}
    >
      {active && (
        <Box
          sx={{
            position: "absolute",
            left: -8,
            top: 6,
            bottom: 6,
            width: 3,
            borderRadius: "0 3px 3px 0",
            backgroundColor: "#f97316",
          }}
        />
      )}
      {Icon ? (
        <Icon
          size={18}
          strokeWidth={active ? 2.25 : 2}
          color={active ? "#f97316" : "#64748b"}
        />
      ) : null}
      <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {item.label}
      </Box>
      {item.badge != null && (
        <Box
          sx={{
            fontSize: 11,
            fontWeight: 600,
            px: "6px",
            py: "1px",
            borderRadius: "6px",
            backgroundColor: active ? "#fff7ed" : "#f1f5f9",
            color: active ? "#c2410c" : "#475569",
          }}
        >
          {item.badge}
        </Box>
      )}
    </Box>
  );
}

function Sidebar({ navSections = [], workspace, user, onItemClick }) {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (to) => {
    if (!to) return false;
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const handleItemClick = (item) => {
    if (item.to) navigate(item.to);
    onItemClick?.(item);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
      }}
    >
      {/* Workspace switcher */}
      {workspace && (
        <Box
          role="button"
          tabIndex={0}
          onClick={workspace.onClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              workspace.onClick?.();
            }
          }}
          sx={{
            m: "12px",
            px: "10px",
            py: "8px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            cursor: workspace.onClick ? "pointer" : "default",
            transition: "background-color 150ms ease-out, border-color 150ms ease-out",
            "&:hover": workspace.onClick
              ? { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" }
              : {},
            "&:focus-visible": { outline: "2px solid #f97316", outlineOffset: 2 },
          }}
        >
          <Avatar
            src={workspace.avatarUrl}
            alt={workspace.name}
            sx={{
              width: 28,
              height: 28,
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: "#fff7ed",
              color: "#c2410c",
            }}
          >
            {workspace.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workspace.name}
            </Box>
          </Box>
          <ChevronsUpDown size={14} color="#94a3b8" />
        </Box>
      )}

      {/* Nav sections */}
      <Box sx={{ flex: 1, overflowY: "auto", pb: "8px" }}>
        {navSections.map((section, idx) => (
          <Box key={section.label || idx}>
            {section.label && <SectionLabel>{section.label}</SectionLabel>}
            {section.items?.filter((item) => !item.hidden).map((item) => (
              <NavItem
                key={item.to || item.label}
                item={item}
                active={isActive(item.to)}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </Box>
        ))}
      </Box>

      {/* Settings nav item */}
      {user && (
        <Box
          role="button"
          tabIndex={0}
          onClick={user.onMenuClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              user.onMenuClick?.();
            }
          }}
          sx={{
            m: "12px",
            px: "12px",
            py: "8px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            backgroundColor: "#ffffff",
            cursor: "pointer",
            transition: "background-color 150ms ease-out, border-color 150ms ease-out",
            "&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
            "&:focus-visible": { outline: "2px solid #f97316", outlineOffset: 2 },
          }}
        >
          <Settings size={18} strokeWidth={2} color="#64748b" />
          <Box
            sx={{
              flex: 1,
              fontSize: 14,
              fontWeight: 500,
              color: "#475569",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Settings
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Sidebar;
